import * as Sentry from '@sentry/react'
import { isDev } from '@twistopayments/design-system/environment'

export type LogEvent = {
  message: string
  level: 'error' | 'warning' | 'info' | 'debug'
  extra?: Record<string, unknown>
}

export type Logger = {
  captureEvent: (event: LogEvent) => void
  captureException: (error: Error, context?: Record<string, unknown>) => void
}

// Composite logger that can use multiple logging platforms
class CompositeLogger implements Logger {
  private loggers: Logger[] = []

  addLogger(logger: Logger) {
    this.loggers.push(logger)
  }

  captureEvent(event: LogEvent) {
    this.loggers.forEach((logger) => {
      try {
        logger.captureEvent(event)
      } catch (error) {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.error('Logger error:', error)
        }
      }
    })
  }

  captureException(error: Error, context?: Record<string, unknown>) {
    this.loggers.forEach((logger) => {
      try {
        logger.captureException(error, context)
      } catch (loggerError) {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.error('Logger error:', loggerError)
        }
      }
    })
  }
}

class SentryLogger implements Logger {
  captureEvent(event: LogEvent) {
    Sentry.captureEvent({
      message: event.message,
      level: event.level,
      extra: event.extra,
    })
  }

  captureException(error: Error, context?: Record<string, unknown>) {
    Sentry.captureException(error, {
      extra: context,
    })
  }
}

// Create the main logger instance
const compositeLogger = new CompositeLogger()

compositeLogger.addLogger(new SentryLogger())

const logError = (message: string, extra?: Record<string, unknown>) => {
  compositeLogger.captureEvent({
    message,
    level: 'error',
    extra,
  })
}

const logWarning = (message: string, extra?: Record<string, unknown>) => {
  compositeLogger.captureEvent({
    message,
    level: 'warning',
    extra,
  })
}

const logInfo = (message: string, extra?: Record<string, unknown>) => {
  compositeLogger.captureEvent({
    message,
    level: 'info',
    extra,
  })
}

const logDebug = (message: string, extra?: Record<string, unknown>) => {
  compositeLogger.captureEvent({
    message,
    level: 'debug',
    extra,
  })
}

const logException = (error: Error, context?: Record<string, unknown>) => {
  compositeLogger.captureException(error, context)
}

export const logger = {
  logDebug,
  logError,
  logException,
  logInfo,
  logWarning,
}
